import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  Calculator, 
  TrendingUp, 
  Clock, 
  Shield, 
  Sparkles, 
  LineChart, 
  Database, 
  Sliders, 
  Award, 
  BarChart3,
  Check,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

const reasonIcons = [
  TrendingUp,
  Shield,
  Clock,
  Sparkles,
  LineChart,
  BarChart3,
  Database,
  Sliders,
  Award,
  Calculator
];

export default function TenReasons() {
  const { t, i18n } = useTranslation('10-reasons');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';
  
  const meta = t('meta', { returnObjects: true }) as { title: string; description: string };
  const hero = t('hero', { returnObjects: true }) as { badge: string; title: string; subtitle: string; cta: string };
  const intro = t('intro', { returnObjects: true }) as { title: string; description: string; benefits: string[] };
  const reasons = t('reasons', { returnObjects: true }) as { title: string; items: Array<{ number: string; title: string; description: string }> };
  const industries = t('industries', { returnObjects: true }) as { title: string; items: Array<{ industry: string; calculator: string; benefit: string }> };
  const seo = t('seo', { returnObjects: true }) as { title: string; description: string; benefits: Array<{ title: string; description: string }> };
  const caseStudy = t('case', { returnObjects: true }) as { title: string; description: string; results: string[]; conclusion: string };
  const tips = t('tips', { returnObjects: true }) as { title: string; items: Array<{ title: string; description: string }> };
  const cta = t('cta', { returnObjects: true }) as { title: string; description: string; features: string[]; button: string };

  const handleContactClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="home" />
      <Header />
      
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>

      {/* Hero Section */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-br from-background via-background to-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-background/50 backdrop-blur-sm mb-6">
              <Calculator className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{hero.badge}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
              {hero.title}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              {hero.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={handleContactClick} data-testid="button-cta-hero">
                {hero.cta}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">{intro.title}</h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">{intro.description}</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {intro.benefits.map((benefit, index) => (
                <Card key={index} className="p-6 text-center">
                  <Check className="h-8 w-8 text-primary mx-auto mb-3" />
                  <p className="font-medium">{benefit}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 10 Reasons Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">{reasons.title}</h2>
          
          <div className="max-w-5xl mx-auto space-y-8">
            {reasons.items.map((reason, index) => {
              const Icon = reasonIcons[index];
              return (
                <Card key={index} className="p-8 hover:shadow-lg transition-shadow">
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl font-bold text-primary/20">{reason.number}</span>
                        <h3 className="text-2xl font-bold">{reason.title}</h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Table Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">{industries.title}</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-background rounded-lg overflow-hidden shadow-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-semibold">
                      {i18n.language === 'en' ? 'Industry' : i18n.language === 'da' ? 'Branche' : i18n.language === 'cs' ? 'Odvětví' : 'Odvetvie'}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {i18n.language === 'en' ? 'Typical Calculator' : i18n.language === 'da' ? 'Typisk Beregner' : i18n.language === 'cs' ? 'Typická Kalkulačka' : 'Typická Kalkulačka'}
                    </th>
                    <th className="text-left p-4 font-semibold">
                      {i18n.language === 'en' ? 'Benefit' : i18n.language === 'da' ? 'Fordel' : i18n.language === 'cs' ? 'Výhoda' : 'Výhoda'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {industries.items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-4 font-medium">{item.industry}</td>
                      <td className="p-4 text-muted-foreground">{item.calculator}</td>
                      <td className="p-4 text-muted-foreground">{item.benefit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">{seo.title}</h2>
            <p className="text-lg text-muted-foreground mb-12 text-center">{seo.description}</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {seo.benefits.map((benefit, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">{caseStudy.title}</h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">{caseStudy.description}</p>
            
            <Card className="p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {caseStudy.results.map((result, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">
                      {result.split(' ')[0]}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {result.split(' ').slice(1).join(' ')}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-center text-muted-foreground italic">{caseStudy.conclusion}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">{tips.title}</h2>
            
            <div className="space-y-4">
              {tips.items.map((tip, index) => (
                <Card key={index} className="p-6 flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{tip.title}</h3>
                    <p className="text-muted-foreground">{tip.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-primary/5 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{cta.title}</h2>
            <p className="text-lg text-muted-foreground mb-8">{cta.description}</p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
              {cta.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            
            <Button size="lg" onClick={handleContactClick} data-testid="button-cta-final">
              {cta.button}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <PageSpecificFAQStructuredData namespace="ten-reasons" />
      <Footer />
    </div>
  );
}
